import React from "react";
import { OrnamentDivider } from "./Ornaments";

const BOOK_COVER =
  "https://customer-assets.emergentagent.com/job_aa602633-f1be-47d7-973a-0c2373de9f76/artifacts/65ziop4f_icm_fullxfull.891787492_89hg604dmxwk44g8wg0w.jpg";

export default function BookFeature() {
  return (
    <section
      id="book"
      data-testid="book-feature"
      className="relative py-24 md:py-32 bg-ocean-base reveal"
      aria-label="About the book Against All Waves"
    >
      <div className="parchment-overlay absolute inset-0" aria-hidden="true" />
      <div className="mist opacity-60" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          {/* Cover */}
          <div className="lg:col-span-5 reveal-left">
            <div
              className="cover-frame candle-glow mx-auto max-w-sm"
              data-parallax="-0.06"
            >
              <img
                src={BOOK_COVER}
                alt="Book cover for Against All Waves, a YA pirate fantasy novel by Chelsea Ray"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
            </div>
            <p className="mt-6 text-center font-accent text-[10px] text-[#c9a25a]/80">
              Cover art by Vivien Gintner
            </p>
          </div>

          {/* Synopsis */}
          <div className="lg:col-span-7 reveal-right" data-stagger="100">
            <p
              data-testid="book-eyebrow"
              data-reveal-child=""
              className="font-accent text-[10px] text-[#c9a25a]"
            >
              A Young Adult Pirate Fantasy
            </p>
            <h2
              data-testid="book-title"
              data-reveal-child=""
              className="font-display text-4xl md:text-5xl lg:text-6xl text-[#f2e8d5] mt-4 leading-[1.02]"
            >
              Against All Waves
            </h2>

            <OrnamentDivider />

            <div className="space-y-5 text-[#cdd6e0] leading-relaxed text-base md:text-[1.05rem]">
              <p>
                As a pirate, and only female crewmember, eighteen-year-old
                <span className="text-[#f2e8d5]"> Elle</span> fights daily to
                prove her worth despite being deemed bad luck. Facing dangerous
                waters, she secretly searches for her mother who had
                mysteriously disappeared. But she must tread carefully, for the
                tyrant captain of <em>The Turbulence</em>, her father{" "}
                <span className="text-[#f2e8d5]">Velis Whitlock</span>, has
                one rule: never speak the name of Elle&rsquo;s mother.
              </p>
              <p>
                <span className="text-[#f2e8d5]">Axel Ardanian&rsquo;s</span>{" "}
                life has been laid out for him since birth. Soon to be ruler of
                the peaceful island of Glendora, he decides to share what the
                island has produced for centuries: a remedy known to cure any
                sickness. But during his travels to other lands, he witnesses
                the harsh realities of poverty and greed, which he fears will
                threaten the land he loves. To protect Glendora, the council
                and his father agree that Axel must unite with one of the
                kingdoms through an advantageous marriage.
              </p>
              <p>
                When <em>The Turbulence</em> targets and attacks Axel&rsquo;s
                ship, his and Elle&rsquo;s lives are suddenly intertwined. In
                the end, both will have to choose to face their fears, protect
                what they hold dear to their hearts, and confront the truth
                that lingers between them.
              </p>
              <p className="text-[#a8b4c0] italic">
                <strong className="text-[#e8d9b6] not-italic">
                  Against All Waves
                </strong>{" "}
                is a young adult pirate fantasy with the perfect mix of
                slow-burn romance, scheming crewmates, deadly sea creatures,
                found family, and a dash of magic.
              </p>
            </div>

            {/* Tags */}
            <ul className="mt-8 flex flex-wrap gap-2.5 reveal-up" aria-label="Book themes" data-stagger="80">
              {[
                "Slow-burn Romance",
                "Found Family",
                "Pirate Fantasy",
                "Deadly Seas",
                "A Dash of Magic",
              ].map((t) => (
                <li
                  key={t}
                  data-reveal-child=""
                  className="font-accent text-[10px] text-[#c9a25a] border border-[#c9a25a]/30 px-3 py-1.5 rounded-full bg-[#0a1118]/40 transition-all duration-500 hover:bg-[#c9a25a]/10 hover:border-[#e8c888] hover:-translate-y-0.5"
                >
                  {t}
                </li>
              ))}
            </ul>

            <div className="mt-10 reveal-scale">
              <a
                data-testid="book-purchase-cta"
                href="https://www.amazon.com/Against-All-Waves-Chelsea-Ray-ebook/dp/B0FGTBDNC8/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold btn-gold-solid"
                aria-label="Purchase Against All Waves on Amazon"
              >
                Purchase Book Here
              </a>
            </div>
          </div>
        </div>

        {/* Upcoming book teaser */}
        <div
          data-testid="upcoming-book"
          className="mt-24 md:mt-32 reveal-up"
          aria-label="Upcoming book Tethered to the Sea, coming 2027"
        >
          <div className="divider-ornament w-full max-w-md mx-auto mb-10">
            <span className="line" />
            <span className="font-accent text-[10px] text-[#c9a25a]/80">
              Coming Soon
            </span>
            <span className="line" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center max-w-4xl mx-auto">
            {/* Placeholder cover */}
            <div className="md:col-span-5 reveal-left">
              <div
                className="upcoming-cover mx-auto max-w-[240px] aspect-[2/3] flex flex-col items-center justify-center text-center px-6"
                aria-hidden="true"
              >
                <span className="font-accent text-[9px] text-[#c9a25a]/80 mb-4">
                  Book Two
                </span>
                <span className="font-display italic text-2xl text-[#f2e8d5] leading-tight">
                  Tethered
                  <br />
                  to the Sea
                </span>
                <span className="mt-6 block w-12 h-px bg-[#c9a25a]/50" />
                <span className="mt-6 font-accent text-[10px] text-[#c9a25a]">
                  Coming 2027
                </span>
              </div>
            </div>

            {/* Teaser copy */}
            <div className="md:col-span-7 reveal-right" data-stagger="100">
              <p
                data-reveal-child=""
                className="font-accent text-[10px] text-[#c9a25a]"
              >
                Book Two — Coming 2027
              </p>
              <h3
                data-reveal-child=""
                data-testid="upcoming-title"
                className="font-display text-3xl md:text-4xl lg:text-5xl text-[#f2e8d5] mt-3 leading-[1.05]"
              >
                Tethered to the Sea
              </h3>
              <p
                data-reveal-child=""
                className="mt-6 text-[#a8b4c0] leading-relaxed max-w-xl"
              >
                The next chapter in Chelsea Ray&rsquo;s seafaring world. New
                tides, fresh stakes, and a story that will pull you under.
                Cover reveal and pre-order details to come.
              </p>
              <div className="mt-8 reveal-scale">
                <a
                  data-testid="upcoming-notify"
                  href="#newsletter"
                  className="btn-gold"
                  aria-label="Get notified when Tethered to the Sea releases"
                >
                  Get Release Updates
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
